/**
 * DocumentTypesSettingsView — Phase 17.2 / 24 / 25 / 85
 *
 * Full CRUD view for the `document_types` table.
 *
 * Columns displayed:
 *   Name | קידומת | להוציא קבלה | חייב במע"מ | השפעת מלאי | מספר התחלתי | פעולות
 *
 * Rules:
 *   • is_system_default = true  → Edit AND Delete buttons are hidden.
 *     A lock badge is shown. inventory_effect is displayed read-only.
 *   • is_system_default = false → full edit + delete + number edit available.
 *     User picks inventory_effect from: none / in / out (transfer is system-only).
 *   • is_number_locked = true   → blue edit icon for next_number is hidden.
 *   • DB-level RLS + SECURITY DEFINER RPC provide defence-in-depth.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, X, Loader as Loader2,
  CircleAlert as AlertCircle, CircleCheck as CheckCircle2,
  FileText, Lock, ShieldCheck, Receipt,
  ReceiptText, Hash, Settings2, Percent,
  ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, MinusCircle,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

export type InventoryEffect = 'in' | 'out' | 'none' | 'transfer' | 'adjustment';

export interface DocumentType {
  id:                        string;
  company_id:                string;
  name:                      string;
  prefix:                    string;
  requires_receipt:          boolean;
  is_vat_liable:             boolean;
  updates_inventory:         boolean;
  inventory_effect:          InventoryEffect;
  is_system_default:         boolean;
  next_number:               number;
  is_number_locked:          boolean;
  has_line_discount:         boolean;
  first_document_issued_at:  string | null;
  created_at:                string;
  updated_at:                string;
}

// ─── Inventory effect helpers ─────────────────────────────────────────────────

const EFFECT_OPTIONS: { value: InventoryEffect; label: string; sublabel: string }[] = [
  { value: 'none', label: 'ללא השפעה',         sublabel: 'המסמך לא משנה את כמויות המלאי' },
  { value: 'in',   label: 'כניסה למלאי (+)',    sublabel: 'המסמך מגדיל את כמויות המלאי' },
  { value: 'out',  label: 'יציאה מהמלאי (−)',   sublabel: 'המסמך מקטין את כמויות המלאי' },
];

function EffectBadge({ effect }: { effect: InventoryEffect }) {
  if (effect === 'in') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
      <ArrowDownToLine className="w-3 h-3" />
      כניסה
    </span>
  );
  if (effect === 'out') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     bg-red-50 text-red-600 text-xs font-medium border border-red-200">
      <ArrowUpFromLine className="w-3 h-3" />
      יציאה
    </span>
  );
  if (effect === 'transfer') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
      <ArrowLeftRight className="w-3 h-3" />
      העברה
    </span>
  );
  if (effect === 'adjustment') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
      <ArrowLeftRight className="w-3 h-3" />
      התאמה
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     bg-gray-50 text-gray-400 text-xs border border-gray-100">
      <MinusCircle className="w-3 h-3" />
      ללא
    </span>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FlagBadge({ value, label }: { value: boolean; label: string }) {
  return value ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                     bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
      <CheckCircle2 className="w-3 h-3" />
      {label}
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full
                     bg-gray-50 text-gray-400 text-xs border border-gray-100">
      —
    </span>
  );
}

interface CheckRowProps {
  label:      string;
  sublabel?:  string;
  icon:       React.ReactNode;
  checked:    boolean;
  onChange:   (v: boolean) => void;
  disabled?:  boolean;
}
function CheckRow({ label, sublabel, icon, checked, onChange, disabled }: CheckRowProps) {
  return (
    <label
      className={`flex items-center justify-between gap-4 px-4 py-3.5 rounded-xl border
                  transition-colors cursor-pointer select-none
                  ${checked ? 'bg-primary-50 border-primary-300' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span className="flex items-center gap-3 min-w-0">
        <span className={checked ? 'text-primary-600' : 'text-gray-400'}>{icon}</span>
        <span className="min-w-0">
          <span className={`block text-sm font-medium ${checked ? 'text-primary-800' : 'text-gray-700'}`}>
            {label}
          </span>
          {sublabel && <span className="block text-xs text-gray-400 mt-0.5">{sublabel}</span>}
        </span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 accent-primary-600 shrink-0"
      />
    </label>
  );
}

/** 3-way inventory effect radio selector — used in add/edit modal */
function EffectSelector({
  value,
  onChange,
  disabled,
}: {
  value: InventoryEffect;
  onChange: (v: InventoryEffect) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2.5">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        השפעת מלאי
      </p>

      {disabled && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200
                        rounded-lg text-xs text-amber-700">
          <Lock className="w-3.5 h-3.5 shrink-0" />
          מסמכי מערכת — ההשפעה קבועה ולא ניתנת לשינוי
        </div>
      )}

      <div className="grid grid-cols-1 gap-2">
        {/* Show transfer option read-only when system doc uses it */}
        {value === 'transfer' ? (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-blue-50 border-blue-300">
            <input type="radio" checked readOnly disabled className="w-4 h-4 accent-blue-600 shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-blue-800">העברה בין מחסנים</span>
              <span className="block text-xs text-gray-400 mt-0.5">
                כניסה למחסן יעד, יציאה ממחסן מקור — לוגיקה ייעודית
              </span>
            </span>
          </div>
        ) : (
          EFFECT_OPTIONS.map(opt => {
            const isSelected = value === opt.value;
            return (
              <label
                key={opt.value}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors
                            ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                            ${isSelected && !disabled
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}
              >
                <input
                  type="radio"
                  name="inventory_effect"
                  value={opt.value}
                  checked={isSelected}
                  disabled={disabled}
                  onChange={() => onChange(opt.value)}
                  className="w-4 h-4 accent-blue-600 shrink-0"
                />
                <span className="min-w-0">
                  <span className={`block text-sm font-medium
                                    ${isSelected && !disabled ? 'text-blue-800' : 'text-gray-700'}`}>
                    {opt.label}
                  </span>
                  <span className="block text-xs text-gray-400 mt-0.5">{opt.sublabel}</span>
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  companyId:  string;
  showToast:  (msg: string, ok?: boolean) => void;
}

const RESERVED_PREFIXES_CACHE: { loaded: boolean; set: Set<string>; reasons: Map<string, string> } = {
  loaded: false,
  set: new Set(),
  reasons: new Map(),
};

async function loadReservedPrefixes(): Promise<void> {
  if (RESERVED_PREFIXES_CACHE.loaded) return;
  const { data } = await supabase.from('reserved_doc_type_prefixes').select('prefix, reason');
  for (const row of data ?? []) {
    const r = row as { prefix: string; reason: string };
    RESERVED_PREFIXES_CACHE.set.add(r.prefix.toUpperCase());
    RESERVED_PREFIXES_CACHE.reasons.set(r.prefix.toUpperCase(), r.reason);
  }
  RESERVED_PREFIXES_CACHE.loaded = true;
}

export function DocumentTypesSettingsView({ companyId, showToast }: Props) {

  // ── Data ──────────────────────────────────────────────────────────────────
  const [rows,    setRows]    = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    const { data, error: dbErr } = await supabase
      .from('document_types')
      .select(`
        id, company_id, name, prefix,
        requires_receipt, is_vat_liable, updates_inventory, inventory_effect,
        is_system_default,
        next_number, is_number_locked,
        has_line_discount,
        first_document_issued_at,
        created_at, updated_at
      `)
      .eq('company_id', companyId)
      .order('is_system_default', { ascending: false })
      .order('name');
    if (dbErr) { setError(dbErr.message); setLoading(false); return; }
    setRows((data ?? []) as DocumentType[]);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // ── Add / Edit modal state ─────────────────────────────────────────────────
  const [modalOpen,      setModalOpen]      = useState(false);
  const [editing,        setEditing]        = useState<DocumentType | null>(null);
  const [formName,       setFormName]       = useState('');
  const [formPrefix,     setFormPrefix]     = useState('');
  const [formReceipt,    setFormReceipt]    = useState(false);
  const [formVat,        setFormVat]        = useState(true);
  const [formEffect,     setFormEffect]     = useState<InventoryEffect>('none');
  const [formNextNumber, setFormNextNumber] = useState<string>('1001');
  const [formError,      setFormError]      = useState<string | null>(null);
  const [saving,         setSaving]         = useState(false);

  const openAdd = () => {
    loadReservedPrefixes();
    setEditing(null);
    setFormName(''); setFormPrefix(''); setFormReceipt(false);
    setFormVat(true); setFormEffect('none'); setFormNextNumber('1001');
    setFormError(null); setModalOpen(true);
  };

  const openEdit = (row: DocumentType) => {
    loadReservedPrefixes();
    setEditing(row);
    setFormName(row.name);
    setFormPrefix(row.prefix ?? '');
    setFormReceipt(row.requires_receipt);
    setFormVat(row.is_vat_liable);
    setFormEffect(row.inventory_effect ?? 'none');
    setFormNextNumber(String(row.next_number ?? 1001));
    setFormError(null); setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditing(null); setFormError(null); };

  const handleSave = async () => {
    const name   = formName.trim();
    const prefix = formPrefix.trim().toUpperCase();
    if (!name)   { setFormError('שם סוג המסמך הוא שדה חובה'); return; }
    if (!prefix) { setFormError('קידומת היא שדה חובה'); return; }
    if (!/^[A-Z]{1,6}$/.test(prefix)) {
      setFormError('קידומת חייבת להכיל 1–6 אותיות לועזיות בלבד (A-Z)');
      return;
    }
    const isEditing = !!editing;
    const editingKeepsSamePrefix = isEditing && editing.prefix.toUpperCase() === prefix;
    if (!editingKeepsSamePrefix && RESERVED_PREFIXES_CACHE.set.has(prefix)) {
      const reason = RESERVED_PREFIXES_CACHE.reasons.get(prefix) ?? '';
      setFormError(
        `הקידומת "${prefix}" שמורה למסמכי ברירת מחדל של המערכת ולא ניתנת לשימוש.` +
        (reason ? `\nסיבה: ${reason}` : '')
      );
      return;
    }
    const parsedNextNumber = parseInt(formNextNumber, 10);
    if (isNaN(parsedNextNumber) || parsedNextNumber < 1 || parsedNextNumber > 9_999_999) {
      setFormError('מספר התחלתי חייב להיות בין 1 ל-9,999,999');
      return;
    }
    setSaving(true); setFormError(null);
    try {
      if (editing) {
        const { error: dbErr } = await supabase
          .from('document_types')
          .update({ name, prefix, requires_receipt: formReceipt, is_vat_liable: formVat })
          .eq('id', editing.id);
        if (dbErr) throw dbErr;

        const { error: rpcErr } = await supabase.rpc('update_document_inventory_effect', {
          p_doc_type_id: editing.id,
          p_effect:      formEffect,
        });
        if (rpcErr) throw rpcErr;

        showToast('סוג מסמך עודכן בהצלחה');
      } else {
        const { error: dbErr } = await supabase
          .from('document_types')
          .insert({
            company_id:        companyId,
            name,
            prefix,
            requires_receipt:  formReceipt,
            is_vat_liable:     formVat,
            inventory_effect:  formEffect,
            updates_inventory: formEffect !== 'none',
            is_system_default: false,
            next_number:       parsedNextNumber,
          });
        if (dbErr) throw dbErr;
        showToast('סוג מסמך נוסף בהצלחה');
      }
      closeModal(); fetchRows();
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      const msg  = e instanceof Error ? e.message
        : typeof e === 'object' && e !== null && 'message' in e
          ? String((e as { message: unknown }).message) : 'שגיאה';
      if (code === '23505' || (msg.toLowerCase().includes('unique') && msg.toLowerCase().includes('prefix'))) {
        setFormError('קידומת זו כבר קיימת במערכת. אנא בחר קידומת ייחודית.');
      } else if (msg.toLowerCase().includes('unique')) {
        setFormError('שם סוג מסמך זה כבר קיים בחברה');
      } else {
        showToast(msg, false);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Column-settings modal state ───────────────────────────────────────────
  const [colTarget,       setColTarget]       = useState<DocumentType | null>(null);
  const [colLineDiscount, setColLineDiscount] = useState(false);
  const [colSaving,       setColSaving]       = useState(false);
  const [colError,        setColError]        = useState<string | null>(null);

  const openColModal  = (row: DocumentType) => { setColTarget(row); setColLineDiscount(row.has_line_discount); setColError(null); };
  const closeColModal = () => { setColTarget(null); setColError(null); };

  const handleColSave = async () => {
    if (!colTarget) return;
    setColSaving(true); setColError(null);
    try {
      const { error: rpcErr } = await supabase.rpc('update_document_discount_setting', {
        p_doc_type_id:       colTarget.id,
        p_has_line_discount: colLineDiscount,
      });
      if (rpcErr) throw rpcErr;
      showToast('הגדרות עמודות עודכנו בהצלחה');
      closeColModal(); fetchRows();
    } catch (e) {
      const msg = e instanceof Error ? e.message
        : typeof e === 'object' && e !== null && 'message' in e
          ? String((e as { message: unknown }).message) : 'שגיאה בשמירת הגדרות';
      setColError(msg);
    } finally {
      setColSaving(false);
    }
  };

  // ── Delete confirmation ───────────────────────────────────────────────────
  const [delTarget, setDelTarget] = useState<DocumentType | null>(null);
  const [deleting,  setDeleting]  = useState(false);

  const handleDelete = async () => {
    if (!delTarget) return;
    setDeleting(true);
    const { error: dbErr } = await supabase.from('document_types').delete().eq('id', delTarget.id);
    if (dbErr) { showToast(dbErr.message, false); }
    else       { showToast('סוג מסמך נמחק'); setDelTarget(null); fetchRows(); }
    setDeleting(false);
  };

  // ── Sequence-edit modal state ─────────────────────────────────────────────
  const [numModalTarget, setNumModalTarget] = useState<DocumentType | null>(null);
  const [numValue,       setNumValue]       = useState<string>('');
  const [numPrefix,      setNumPrefix]      = useState<string>('');
  const [numError,       setNumError]       = useState<string | null>(null);
  const [numSaving,      setNumSaving]      = useState(false);

  const openNumModal  = (row: DocumentType) => { setNumModalTarget(row); setNumValue(String(row.next_number)); setNumPrefix(row.prefix ?? ''); setNumError(null); };
  const closeNumModal = () => { setNumModalTarget(null); setNumError(null); };

  const handleNumSave = async () => {
    if (!numModalTarget) return;
    const cleanPrefix = numPrefix.trim().toUpperCase();
    if (!cleanPrefix || !/^[A-Z]{1,5}$/.test(cleanPrefix)) {
      setNumError('קידומת חייבת להכיל 1–5 אותיות לועזיות (A-Z)');
      return;
    }
    const parsed = parseInt(numValue, 10);
    if (isNaN(parsed) || parsed < 1 || parsed > 9_999_999) {
      setNumError('יש להזין מספר שלם בין 1 ל-9,999,999');
      return;
    }
    setNumSaving(true); setNumError(null);
    try {
      const { error: rpcErr } = await supabase.rpc('update_document_sequencing', {
        p_doc_type_id: numModalTarget.id,
        p_new_prefix:  cleanPrefix,
        p_new_number:  parsed,
      });
      if (rpcErr) throw rpcErr;
      showToast('רצף המסמך עודכן בהצלחה');
      closeNumModal(); fetchRows();
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      const msg  = e instanceof Error ? e.message
        : typeof e === 'object' && e !== null && 'message' in e
          ? String((e as { message: unknown }).message) : 'שגיאה בעדכון';
      if (code === '23505' || (msg.toLowerCase().includes('unique') && msg.toLowerCase().includes('prefix'))) {
        setNumError('קידומת זו כבר קיימת במערכת. אנא בחר קידומת ייחודית.');
      } else {
        setNumError(msg);
      }
    } finally {
      setNumSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-blue-500" />
          <div>
            <h2 className="text-base font-semibold text-gray-900">סוגי מסמכים</h2>
            <p className="text-xs text-gray-500">
              ניהול סוגי תעודות החברה — הזמנות, משלוחים, חשבוניות וכדומה
            </p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700
                     text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          הוסף סוג מסמך
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-3 mx-6 my-4 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-14 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">טוען סוגי מסמכים…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
          <FileText className="w-12 h-12 text-gray-200" />
          <p className="text-sm font-medium">אין סוגי מסמכים מוגדרים</p>
          <p className="text-xs text-gray-400">לחץ על "הוסף סוג מסמך" כדי להתחיל</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold
                             text-gray-500 uppercase tracking-wide">
                <th className="px-6 py-3 text-right">שם המסמך</th>
                <th className="px-4 py-3 text-center">להוציא קבלה</th>
                <th className="px-4 py-3 text-center">חייב במע״מ</th>
                <th className="px-4 py-3 text-center">השפעת מלאי</th>
                <th className="px-4 py-3 text-center">קידומת</th>
                <th className="px-4 py-3 text-center">מספר התחלתי</th>
                <th className="px-4 py-3 text-right w-28">פעולות</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50/60 transition-colors">

                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{row.name}</span>
                      {row.is_system_default && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5
                                         rounded-full bg-amber-50 text-amber-600
                                         text-[10px] font-medium border border-amber-200">
                          <Lock className="w-2.5 h-2.5" />
                          ברירת מחדל
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <FlagBadge value={row.requires_receipt} label="קבלה" />
                  </td>
                  <td className="px-4 py-4 text-center">
                    <FlagBadge value={row.is_vat_liable} label="מע״מ" />
                  </td>
                  <td className="px-4 py-4 text-center">
                    <EffectBadge effect={row.inventory_effect ?? 'none'} />
                  </td>

                  <td className="px-4 py-4 text-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md
                                     bg-gray-100 text-gray-700 text-xs font-mono font-bold
                                     border border-gray-200">
                      {row.prefix || '—'}
                    </span>
                  </td>

                  <td className="px-4 py-4 text-center">
                    <div className="inline-flex items-center gap-2 justify-center">
                      <span className="font-mono text-sm font-semibold text-gray-700">
                        {row.prefix || ''}{row.next_number ?? 1001}
                      </span>
                      {row.first_document_issued_at ? (
                        <span title="נעול — מסמך ראשון כבר הופק">
                          <Lock className="w-3.5 h-3.5 text-amber-400" />
                        </span>
                      ) : !row.is_number_locked ? (
                        <button
                          onClick={() => openNumModal(row)}
                          className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50
                                     rounded-md transition-colors"
                          title="ערוך קידומת ומספר התחלתי"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <span title="המספר הרץ נעול">
                          <Lock className="w-3.5 h-3.5 text-gray-300" />
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openColModal(row)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                   rounded-lg transition-colors"
                        title="הגדרת עמודות"
                      >
                        <Settings2 className="w-4 h-4" />
                      </button>

                      {row.is_system_default ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg
                                     bg-gray-50 text-gray-400 text-xs border border-gray-100"
                          title="מסמכי מערכת אינם ניתנים לעריכה"
                        >
                          <Lock className="w-3 h-3" />
                          נעול
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={() => openEdit(row)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50
                                       rounded-lg transition-colors"
                            title="ערוך"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDelTarget(row)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50
                                       rounded-lg transition-colors"
                            title="מחק"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="px-6 py-3 border-t border-gray-50 text-xs text-gray-400">
            {rows.length} סוגי מסמכים ·{' '}
            <span className="inline-flex items-center gap-1">
              <Lock className="w-3 h-3" />
              מסמכי ברירת מחדל — ההשפעה על המלאי קבועה ולא ניתנת לשינוי
            </span>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          ADD / EDIT MODAL
          ═══════════════════════════════════════════════════════════ */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col
                          max-h-[90vh]">

            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-bold text-gray-900">
                  {editing ? 'עריכת סוג מסמך' : 'הוספת סוג מסמך'}
                </h3>
              </div>
              <button onClick={closeModal} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

              {editing?.is_system_default && (
                <div className="flex items-start gap-2.5 px-3 py-2.5 bg-red-50
                                border border-red-200 rounded-xl text-xs text-red-700">
                  <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>מסמכי מערכת אינם ניתנים לעריכה. סגור חלון זה.</span>
                </div>
              )}

              {formError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                                rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{formError}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  שם המסמך <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSave()}
                  placeholder='לדוגמה: חשבונית מס, תעודת משלוח…'
                  autoFocus
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>

              {!editing?.is_system_default && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      קידומת (Prefix) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formPrefix}
                      onChange={e => setFormPrefix(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5))}
                      placeholder='לדוגמה: INV, DN, SO…'
                      maxLength={5}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 font-mono uppercase focus:outline-none focus:ring-2
                                 focus:ring-blue-400 focus:border-transparent tracking-widest"
                      dir="ltr"
                    />
                    <p className="text-xs text-gray-400">
                      1–5 אותיות לועזיות בלבד, ייחודי לכל חברה. תוצג לפני המספר:{' '}
                      <span className="font-mono font-semibold text-blue-700">
                        {formPrefix || 'XXX'}{formNextNumber || '1001'}
                      </span>
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      מספר התחלתי <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={9999999}
                      value={formNextNumber}
                      onChange={e => setFormNextNumber(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                                 font-mono focus:outline-none focus:ring-2 focus:ring-blue-400
                                 focus:border-transparent"
                      dir="ltr"
                      placeholder="לדוגמה: 1001"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  הגדרות התנהגות
                </p>
                <CheckRow
                  label="להוציא קבלה"
                  sublabel="מסמך זה מלווה תמיד בקבלה על תשלום"
                  icon={<Receipt className="w-4 h-4" />}
                  checked={formReceipt}
                  onChange={setFormReceipt}
                  disabled={!!editing?.is_system_default}
                />
                <CheckRow
                  label='חייב במע"מ'
                  sublabel="המסמך יכלול מע״מ (בכפוף לסטטוס הלקוח)"
                  icon={<ReceiptText className="w-4 h-4" />}
                  checked={formVat}
                  onChange={setFormVat}
                  disabled={!!editing?.is_system_default}
                />
              </div>

              <EffectSelector
                value={formEffect}
                onChange={setFormEffect}
                disabled={!!editing?.is_system_default}
              />

            </div>

            <div className="flex items-center gap-3 justify-end px-6 py-4
                            border-t border-gray-100 shrink-0">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100
                           hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !!editing?.is_system_default}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700
                           text-white text-sm font-medium rounded-lg transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 animate-spin" />שומר…</>
                  : <><CheckCircle2 className="w-4 h-4" />שמור</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          COLUMN SETTINGS MODAL
          ═══════════════════════════════════════════════════════════ */}
      {colTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4">

            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-blue-500" />
                <div>
                  <h3 className="text-base font-bold text-gray-900">הגדרת עמודות</h3>
                  <p className="text-xs text-gray-400">{colTarget.name}</p>
                </div>
              </div>
              <button onClick={closeColModal} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-3">
              <p className="text-xs text-gray-500">
                בחר אילו עמודות דינמיות יופיעו בטופס הפקת המסמך מסוג זה.
              </p>

              {colError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                                rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{colError}
                </div>
              )}

              <label className={`flex items-center justify-between gap-4 px-4 py-3.5 rounded-xl
                                 border cursor-pointer select-none transition-colors
                                 ${colLineDiscount
                                    ? 'bg-amber-50 border-amber-300'
                                    : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}>
                <span className="flex items-center gap-3">
                  <Percent className={`w-4 h-4 ${colLineDiscount ? 'text-amber-600' : 'text-gray-400'}`} />
                  <span className="min-w-0">
                    <span className={`block text-sm font-medium ${colLineDiscount ? 'text-amber-800' : 'text-gray-700'}`}>
                      עמודת הנחת שורה (%)
                    </span>
                    <span className="block text-xs text-gray-400 mt-0.5">
                      מאפשר הזנת הנחה אחוזית לכל פריט בשורה
                    </span>
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={colLineDiscount}
                  onChange={e => setColLineDiscount(e.target.checked)}
                  className="w-4 h-4 accent-amber-500 shrink-0"
                />
              </label>
            </div>

            <div className="flex items-center gap-3 justify-end px-6 py-4 border-t border-gray-100">
              <button
                onClick={closeColModal}
                disabled={colSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100
                           hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                onClick={handleColSave}
                disabled={colSaving}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700
                           text-white text-sm font-medium rounded-lg transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {colSaving
                  ? <><Loader2 className="w-4 h-4 animate-spin" />שומר…</>
                  : <><CheckCircle2 className="w-4 h-4" />שמור</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          DELETE CONFIRMATION MODAL
          ═══════════════════════════════════════════════════════════ */}
      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 space-y-4">

            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-xl shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">מחיקת סוג מסמך</h3>
                <p className="text-sm text-gray-600 mt-1">
                  האם למחוק את <strong>"{delTarget.name}"</strong>?
                  פעולה זו אינה ניתנת לביטול.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setDelTarget(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100
                           hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700
                           text-white text-sm font-medium rounded-lg transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting
                  ? <><Loader2 className="w-4 h-4 animate-spin" />מוחק…</>
                  : <><Trash2 className="w-4 h-4" />מחק</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          STARTING NUMBER EDIT MODAL
          ═══════════════════════════════════════════════════════════ */}
      {numModalTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 flex flex-col">

            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Hash className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-bold text-gray-900">עריכת רצף מסמך</h3>
              </div>
              <button onClick={closeNumModal} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-600">
                סוג מסמך: <strong>{numModalTarget.name}</strong>
              </p>

              {numError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200
                                rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />{numError}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  קידומת (Prefix) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={numPrefix}
                  onChange={e => setNumPrefix(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5))}
                  maxLength={5}
                  autoFocus
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             font-mono uppercase tracking-widest focus:outline-none focus:ring-2
                             focus:ring-blue-400 focus:border-transparent"
                  dir="ltr"
                  placeholder="לדוגמה: INV"
                />
                <p className="text-xs text-gray-400">1–5 אותיות לועזיות בלבד, ייחודי לחברה</p>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">
                  מספר התחלתי <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min={1}
                  max={9999999}
                  value={numValue}
                  onChange={e => setNumValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleNumSave()}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                             font-mono text-left ltr focus:outline-none focus:ring-2
                             focus:ring-blue-400 focus:border-transparent"
                  dir="ltr"
                />
                <p className="text-xs text-gray-400 leading-relaxed">
                  המסמך הבא יופיע כ:{' '}
                  <span className="font-mono font-semibold text-blue-700">
                    {numPrefix || numModalTarget.prefix || 'XXX'}{numValue || '1001'}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 justify-end px-6 py-4 border-t border-gray-100">
              <button
                onClick={closeNumModal}
                disabled={numSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100
                           hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                onClick={handleNumSave}
                disabled={numSaving}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700
                           text-white text-sm font-medium rounded-lg transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {numSaving
                  ? <><Loader2 className="w-4 h-4 animate-spin" />שומר…</>
                  : <><CheckCircle2 className="w-4 h-4" />שמור</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
}
